import type { JSX } from 'solid-js';
import type { SplitDirection } from '../../lib/terminal/layout';
import { MAX_RATIO, MIN_RATIO } from '../../lib/terminal/layout';

// The pane tree's resize handle (#4397, epic #4391) — `setRatioAtPath`
// (`lib/terminal/layout.ts`) is a pure function; this is the one place that
// calls it from a real interaction, so the op is reachable rather than an
// inert prescription nothing ever invokes.
//
// KEYBOARD IS THE PRIMARY, ALWAYS-CORRECT PATH. `role="separator"` +
// `tabindex="0"` + arrow keys, per the ARIA window-splitter pattern — correct
// at every viewport width, because it works on `ratio` directly and never
// has to know which way the divider is drawn.
//
// POINTER DRAG FOLLOWS THE LOGICAL `dir`, WITH ONE KNOWN ROUGH EDGE. Below
// `respond-to('lg')` every split renders STACKED regardless of `dir`
// (`_terminal.scss`'s responsive rule, issue step 5) — a `row` split's
// divider is drawn horizontally there even though `dir` still says `row`.
// Reading the true rendered axis would mean measuring live layout from
// script; simplest instead to accept that a `row` split's pointer-drag below
// `lg` follows the WRONG screen axis until this is revisited, and lean on the
// keyboard path (which has no such gap) as this control's fully-correct
// interaction at every width. Flagged rather than silently shipped.
export function TerminalPaneDivider(props: {
  readonly dir: SplitDirection;
  readonly ratio: number;
  readonly onChange: (ratio: number) => void;
  readonly label: string;
  /**
   * Hands the started drag's idempotent `end` to the owner that outlives this
   * divider (`TerminalPaneTree`), so an unmount mid-drag removes the window
   * listeners; returns the release this drag calls when it ends on its own. Not
   * this component's own `onCleanup`: every move replaces this divider (below),
   * and its cleanup would end the drag after one pixel.
   */
  readonly holdDrag: (end: () => void) => () => void;
}): JSX.Element {
  const step = 0.05;
  const isRow = (): boolean => props.dir === 'row';

  // `rect`, not a live `container` re-measured per move: the CONTAINER's own
  // box doesn't move during a resize drag (only the ratio splitting it does),
  // and a live re-measure would hit the same stale-DOM-node problem
  // `onPointerDown` below documents — a rect computed once, before the first
  // remount can happen, has nothing to go stale.
  function ratioFromPointer(e: PointerEvent, rect: DOMRect): number {
    return isRow() ? (e.clientX - rect.left) / rect.width : (e.clientY - rect.top) / rect.height;
  }

  // WINDOW-LEVEL, NOT THE HANDLE — found in review. `props.onChange` on the
  // first `pointermove` calls `setLayout`, whose new `layout().root` is
  // `TerminalPaneTree`'s tree memo's only tracked dependency: the memo
  // reruns `renderNode` and replaces this `<hr>` (a plain recursive call,
  // never a keyed `<For>`) with a brand-new DOM element. A listener attached
  // to the handle itself — and its `setPointerCapture`, which the Pointer
  // Events spec releases the instant its target leaves the DOM — dies with
  // the old node, so a drag stopped updating after exactly one pixel of
  // movement. `window` never gets replaced, so it keeps receiving the
  // pointer stream (bubbling delivers it regardless of capture) for the
  // rest of the drag.
  function onPointerDown(e: PointerEvent): void {
    const handle = e.currentTarget as HTMLHRElement;
    const container = handle.parentElement;
    if (container === null) return;
    e.preventDefault();
    const rect = container.getBoundingClientRect();

    function onMove(moveEvent: PointerEvent): void {
      props.onChange(ratioFromPointer(moveEvent, rect));
    }
    function onUp(): void {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      release();
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    const release = props.holdDrag(onUp);
  }

  function onKeyDown(e: KeyboardEvent): void {
    const decrease = isRow() ? 'ArrowLeft' : 'ArrowUp';
    const increase = isRow() ? 'ArrowRight' : 'ArrowDown';
    if (e.key === decrease) {
      e.preventDefault();
      props.onChange(props.ratio - step);
    } else if (e.key === increase) {
      e.preventDefault();
      props.onChange(props.ratio + step);
    } else if (e.key === 'Home') {
      e.preventDefault();
      props.onChange(MIN_RATIO);
    } else if (e.key === 'End') {
      e.preventDefault();
      props.onChange(MAX_RATIO);
    }
  }

  return (
    // `<hr>` carries the `separator` role implicitly (biome's own
    // `useSemanticElements` suggestion) and takes the same ARIA/pointer/key
    // attributes a `<div role="separator">` would.
    <hr
      class="terminal__divider"
      classList={{ 'terminal__divider--row': isRow(), 'terminal__divider--column': !isRow() }}
      aria-orientation={isRow() ? 'vertical' : 'horizontal'}
      aria-valuemin={Math.round(MIN_RATIO * 100)}
      aria-valuemax={Math.round(MAX_RATIO * 100)}
      aria-valuenow={Math.round(props.ratio * 100)}
      aria-label={props.label}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    />
  );
}
